// 通用错误处理工具
import { isWechatBrowser } from '../config/wechat';
import { shouldAutoLogout } from './pageAuth';
import toastManager from './toastManager';

// 防重复错误处理机制
let lastErrorTime = 0;
let lastErrorMessage = '';
const ERROR_DEBOUNCE_TIME = 2000; // 2秒内不重复显示相同错误

// 检查是否是认证错误或服务器错误
export const isAuthError = (error, responseData) => {
  // 检查错误消息
  const errorMessage = error?.message || '';
  const hasAuthErrorKeywords = errorMessage.includes('401') || 
                              errorMessage.includes('500') ||
                              errorMessage.includes('No value present') ||
                              errorMessage.includes('登录已过期') ||
                              errorMessage.includes('未登录') ||
                              errorMessage.includes('无权限');
  
  // 检查响应数据
  const hasAuthResponseError = responseData && (
    (responseData.code === 401 || responseData.code === 500) && 
    responseData.message === 'No value present'
  );
  
  // 检查HTTP状态码
  const hasAuthStatusCode = error?.status === 401 || error?.status === 500;
  
  return hasAuthErrorKeywords || hasAuthResponseError || hasAuthStatusCode;
};

// 防重复显示错误
const shouldShowError = (message) => {
  const now = Date.now();
  if (message === lastErrorMessage && (now - lastErrorTime) < ERROR_DEBOUNCE_TIME) {
    console.log('防重复：跳过相同错误消息:', message);
    return false;
  }
  lastErrorTime = now;
  lastErrorMessage = message;
  return true;
};

// 处理认证错误
export const handleAuthError = (error, responseData = null) => {
  console.log('处理认证错误:', { error, responseData });
  
  // 清除所有登录相关的本地存储
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('token');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('avatar');
  localStorage.removeItem('accountNickName');
  localStorage.removeItem('hasEditAllPermission');
  localStorage.removeItem('lastActivity');
  
  // 清除微信相关数据（可选）
  localStorage.removeItem('wechatUser');
  localStorage.removeItem('wechatToken');
  localStorage.removeItem('isWechatLoggedIn');
  
  // 检查是否是微信环境
  const fromWechatForm = localStorage.getItem('fromWechatForm');
  const isWechat = isWechatBrowser();
  
  if (fromWechatForm === '1' || isWechat) {
    // 微信表单场景
    console.log('微信表单场景，跳转到微信引导页');
    return {
      shouldRedirect: false,
      message: '微信环境下的认证错误'
    };
  } else {
    // 普通场景，检查当前页面是否需要自动退出
    console.log('普通场景，检查当前页面是否需要自动退出');
    
    if (!shouldAutoLogout()) {
      console.log('当前页面不需要自动退出');
      return {
        shouldRedirect: false,
        message: '当前页面不需要自动退出'
      };
    }
    
    console.log('当前页面需要自动退出，执行退出登录');
    if (window.location.pathname !== '/login') {
      // 设置手动退出标识，防止自动授权
      localStorage.setItem('manualLogout', 'true');
      // 直接跳转到登录页，不显示确认弹窗
      window.location.href = '/login';
    }
    return {
      shouldRedirect: true,
      message: '登录已过期，请重新登录'
    };
  }
};

// 通用API错误处理
export const handleApiError = (error, responseData = null, showToast = true) => {
  console.error('API错误:', error);
  
  // 检查是否是认证错误
  if (isAuthError(error, responseData)) {
    const result = handleAuthError(error, responseData);
    if (showToast && result.message && shouldShowError(result.message)) {
      // 使用toastManager显示错误信息
      toastManager.error(result.message);
    }
    return result;
  }
  
  // 其他错误处理
  const errorMessage = error?.message || '请求失败，请稍后重试';
  if (showToast && shouldShowError(errorMessage)) {
    toastManager.error(errorMessage);
  }
  
  return {
    shouldRedirect: false,
    message: errorMessage
  };
};

// 检查响应数据中的错误
export const checkResponseError = (response, data) => {
  // 检查HTTP状态码
  if (response.status === 401 || response.status === 500) {
    return {
      isError: true,
      type: 'auth',
      message: '认证错误或服务器错误'
    };
  }
  
  // 检查响应数据中的错误码
  if (data && (data.code === 401 || data.code === 500)) {
    return {
      isError: true,
      type: 'auth',
      message: data.message || '认证错误或服务器错误'
    };
  }
  
  // 检查"No value present"错误
  if (data && data.message === 'No value present') {
    return {
      isError: true,
      type: 'auth',
      message: '认证信息无效'
    };
  }
  
  return {
    isError: false,
    type: null,
    message: null
  };
};

// 清除错误防重复状态
export const clearErrorDebounce = () => {
  lastErrorTime = 0;
  lastErrorMessage = '';
};

export default {
  isAuthError,
  handleAuthError,
  handleApiError,
  checkResponseError,
  clearErrorDebounce
}; 